import React from 'react';
import {
  Grid, Paper, Typography, Stack, Box, Divider, Card, CardContent,
  Chip, Avatar, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import BookIcon from '@mui/icons-material/Book';

const SubjectOverviewTab = ({ subject, schedules, subjectColor }) => {
  // Calculate statistics
  const totalSchedules = schedules?.length || 0;
  const totalHours = schedules?.reduce((sum, s) => sum + (s.duration_minutes || 0), 0) / 60 || 0;
  const uniqueClasses = new Set(schedules?.map(s => s.school_class_name).filter(Boolean)).size;
  const uniqueDays = new Set(schedules?.map(s => s.day_of_week).filter(Boolean)).size;

  // Group schedules by class
  const schedulesByClass = {};
  schedules?.forEach(schedule => {
    const className = schedule.school_class_name || 'غير محدد';
    if (!schedulesByClass[className]) {
      schedulesByClass[className] = [];
    }
    schedulesByClass[className].push(schedule);
  });

  // Get day name in Arabic
  const getDayName = (dayValue) => {
    const days = {
      'sunday': 'الأحد',
      'monday': 'الاثنين',
      'tuesday': 'الثلاثاء',
      'wednesday': 'الأربعاء',
      'thursday': 'الخميس',
      'friday': 'الجمعة',
      'saturday': 'السبت'
    };
    return days[dayValue] || dayValue;
  };

  return (
    <Grid container spacing={3}>
      {/* Statistics Cards */}
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', p: 2 }}>
          <Avatar sx={{ bgcolor: subjectColor, mx: 'auto', mb: 1, width: 56, height: 56 }}>
            <CalendarTodayIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color={subjectColor}>
            {totalSchedules}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            حصة أسبوعية
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', p: 2 }}>
          <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1, width: 56, height: 56 }}>
            <AccessTimeIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {totalHours.toFixed(1)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ساعة أسبوعياً
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', p: 2 }}>
          <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 1, width: 56, height: 56 }}>
            <SchoolIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="info.main">
            {uniqueClasses}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            صف دراسي
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', p: 2 }}>
          <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 1, width: 56, height: 56 }}>
            <BookIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="warning.main">
            {uniqueDays}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            أيام في الأسبوع
          </Typography>
        </Card>
      </Grid>

      {/* Subject Information */}
      <Grid item xs={12} md={6}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            معلومات المادة
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <BookIcon sx={{ color: subjectColor }} />
              </ListItemIcon>
              <ListItemText
                primary="اسم المادة"
                secondary={subject?.name || 'غير محدد'}
              />
            </ListItem>
            {subject?.school_class_name && (
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <SchoolIcon sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="الصف الدراسي"
                  secondary={subject.school_class_name}
                />
              </ListItem>
            )}
            {subject?.teacher_name && (
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <PersonIcon sx={{ color: 'secondary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="المعلم المسؤول"
                  secondary={subject.teacher_name}
                />
              </ListItem>
            )}
            {subject?.hours_per_week && (
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <AccessTimeIcon sx={{ color: 'info.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="الساعات الأسبوعية"
                  secondary={`${subject.hours_per_week} ساعة`}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Grid>

      {/* Schedules by Class */}
      <Grid item xs={12} md={6}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            الجدول حسب الصف
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {Object.keys(schedulesByClass).length > 0 ? (
            <Stack spacing={2}>
              {Object.entries(schedulesByClass).map(([className, classSchedules]) => (
                <Box key={className}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {className}
                  </Typography>
                  <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
                    {classSchedules.map((schedule, idx) => (
                      <Chip
                        key={idx}
                        size="small"
                        label={`${getDayName(schedule.day_of_week)} ${schedule.start_time || ''}`}
                        sx={{ bgcolor: `${subjectColor}20`, color: subjectColor }}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              لا توجد حصص مجدولة
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SubjectOverviewTab;
